// ============================================
// 编辑器左侧属性面板持久化（2026-09-27）
// 背景：刷新浏览器后左侧属性面板回到"请选择左侧功能菜单"空态（右侧模型属性已由
//       useSelectionPersistence 恢复），用户需重新点选功能菜单、重新展开构件树。
// 方案：按场景把「左侧功能菜单定位 + 模型面板构件树展开态 + 组合模型解锁态」存
//       localStorage，场景模型就绪后恢复一次；恢复前校验（面板 key 白名单、模型仍存在）。
// ============================================

import { useEffect, useRef } from 'react';
import useEditorUiStore from '../stores/editorUiStore';
import useSceneStore from '../stores/sceneStore';
import useGisStore from '../stores/gisStore';

const KEY_PREFIX = 'dtwin-editor-panel:';

/** 左侧功能菜单合法 key（与 PropertyTreePanel 菜单项一致；非法值不恢复，防脏档） */
export const PANEL_KEYS = [
  'scene',
  'models',
  'model-library',
  'tools',
  'layers',
  'measurement',
  'environment',
  'effects',
  'lights',
  'timeline',
  'gis',
  'camera',
  'carousel',
  'clipping',
  'flylines',
  'iot',
  'data',
  // 2026-10-15（P0-④）：鸟瞰相机属性面板
  'birdsEye',
] as const;

export interface PanelPrefs {
  activePanel: string | null;
  expandedModelId: string | null;
  subObjectUnlocked: Record<string, boolean>;
  /** 2026-10-05：大屏叠加编辑态（按场景持久化，刷新后可恢复） */
  overlayEditMode?: boolean;
  /** 2026-10-05：大屏叠加编辑右侧 Tab（属性配置/图层） */
  overlayRightTab?: 'props' | 'layers';
  /** 2026-10-15（P0-③ 场景树）：展开的分类/节点 key（按场景持久化） */
  sceneTreeCollapsed?: string[];
}

export function panelStorageKey(sceneId: string): string {
  return `${KEY_PREFIX}${sceneId}`;
}

/** 解析存档（容错：非法 JSON/非法面板 key → 不恢复；未知模型 id 由恢复时按模型列表过滤） */
export function parseSavedPanelPrefs(raw: string | null): PanelPrefs | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
    return {
      activePanel:
        typeof obj.activePanel === 'string' && (PANEL_KEYS as readonly string[]).includes(obj.activePanel)
          ? obj.activePanel
          : null,
      expandedModelId: typeof obj.expandedModelId === 'string' ? obj.expandedModelId : null,
      subObjectUnlocked:
        obj.subObjectUnlocked && typeof obj.subObjectUnlocked === 'object' && !Array.isArray(obj.subObjectUnlocked)
          ? obj.subObjectUnlocked
          : {},
      overlayEditMode: obj.overlayEditMode === true,
      overlayRightTab: obj.overlayRightTab === 'layers' ? 'layers' : obj.overlayRightTab === 'props' ? 'props' : undefined,
      sceneTreeCollapsed: Array.isArray(obj.sceneTreeCollapsed) ? obj.sceneTreeCollapsed.filter((k: unknown) => typeof k === 'string') : undefined,
    };
  } catch {
    return null;
  }
}

export function readSavedPanelPrefs(sceneId: string): PanelPrefs | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return parseSavedPanelPrefs(localStorage.getItem(panelStorageKey(sceneId)));
  } catch {
    return null;
  }
}

export function writeSavedPanelPrefs(sceneId: string, prefs: PanelPrefs): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(panelStorageKey(sceneId), JSON.stringify(prefs));
  } catch {
    /* 忽略存储失败 */
  }
}

/** 从 store 取当前面板偏好写回（统一入口，用 getState 取最新值，避免同批次旧闭包覆盖）
 *  2026-09-27：getState 可选调用——部分单测 mock store 未提供 getState（沿用 PropertyTreePanel 先例）
 *  2026-10-05：overlay 恢复完成前保留存档中的 overlay 字段（避免挂载初写回把叠加编辑态覆盖为 false） */
function writePanelPrefsFromStores(sceneId: string, includeOverlay = false): void {
  const uiAny = useEditorUiStore as any;
  const sceneAny = useSceneStore as any;
  const ui = uiAny.getState?.();
  const scene = sceneAny.getState?.();
  if (!ui || !scene) return;
  const prev = readSavedPanelPrefs(sceneId);
  const prefs: PanelPrefs = {
    ...(prev || { subObjectUnlocked: {} }),
    activePanel: ui.activePanel ?? null,
    expandedModelId: scene.expandedModelId ?? null,
    subObjectUnlocked: scene.subObjectUnlocked ?? {},
  };
  if (includeOverlay) {
    prefs.overlayEditMode = ui.overlayEditMode === true;
    prefs.overlayRightTab = ui.overlayRightTab === 'layers' ? 'layers' : 'props';
  }
  writeSavedPanelPrefs(sceneId, prefs);
}

/**
 * 在场景编辑器内调用：恢复上次左侧功能菜单定位（'gis' 定位联动开启 GIS 模式）与
 * 模型面板构件树展开/解锁态；之后变化写回（恢复完成后才写，避免初始空值覆盖存档）。
 * @param sceneId 场景 id（切换场景时按新场景重新恢复）
 */
export function usePanelPersistence(sceneId: string | undefined): void {
  const activePanel = useEditorUiStore((s) => s.activePanel);
  const overlayEditMode = useEditorUiStore((s) => s.overlayEditMode);
  const overlayRightTab = useEditorUiStore((s) => s.overlayRightTab);
  const dashboardId = useEditorUiStore((s) => s.dashboardId);
  const currentSceneId = useSceneStore((s) => s.currentScene?.id);
  const modelCount = useSceneStore((s) => s.models.length);
  const expandedModelId = useSceneStore((s) => s.expandedModelId);
  const subObjectUnlocked = useSceneStore((s) => s.subObjectUnlocked);
  const panelRestoredRef = useRef(false);
  const modelPrefsRestoredRef = useRef(false);
  const overlayRestoredRef = useRef(false);

  // 切换场景：允许对新场景重新恢复
  useEffect(() => {
    panelRestoredRef.current = false;
    modelPrefsRestoredRef.current = false;
    overlayRestoredRef.current = false;
  }, [sceneId]);

  // 恢复①：左侧功能菜单定位（有存档才应用，保留当前定位兜底；GIS 定位需联动开启 GIS 模式）
  useEffect(() => {
    if (panelRestoredRef.current || !sceneId) return;
    panelRestoredRef.current = true;
    const saved = readSavedPanelPrefs(sceneId);
    if (!saved?.activePanel) return;
    const uiAny = useEditorUiStore as any;
    uiAny.getState?.()?.setActivePanel?.(saved.activePanel);
    if (saved.activePanel === 'gis') {
      const gisAny = useGisStore as any;
      gisAny.getState?.()?.setGisEnabled?.(true);
    }
  }, [sceneId]);

  // 恢复②：构件树展开态 + 解锁态（等本场景加载完成；校验模型仍存在，避免脏 id）
  // 2026-09-27 修复（A05 审计发现）：原条件要求 modelCount>0，而写回①在挂载时即触发（此时模型偏好尚未恢复）
  // → 存档被初始空值覆盖（expandedModelId/subObjectUnlocked 丢失）。改为场景加载完成即恢复，且写回门控双 ref。
  useEffect(() => {
    if (modelPrefsRestoredRef.current || !sceneId || currentSceneId !== sceneId) return;
    modelPrefsRestoredRef.current = true;
    const saved = readSavedPanelPrefs(sceneId);
    const sceneAny = useSceneStore as any;
    const ids = new Set<string>((sceneAny.getState?.()?.models ?? []).map((m: { id: string }) => m.id));
    const expanded =
      saved?.expandedModelId && ids.has(saved.expandedModelId) ? saved.expandedModelId : null;
    const unlocked: Record<string, boolean> = {};
    Object.entries(saved?.subObjectUnlocked ?? {}).forEach(([id, v]) => {
      if (v === true && ids.has(id)) unlocked[id] = true;
    });
    sceneAny.getState?.()?.setExpandedModelId?.(expanded);
    sceneAny.getState?.()?.setSubObjectUnlockedMap?.(unlocked);
  }, [sceneId, currentSceneId, modelCount]);

  // 恢复③：大屏叠加编辑态 + 右侧 Tab（2026-10-05；需已解析到关联大屏，避免无大屏时误入）
  useEffect(() => {
    if (overlayRestoredRef.current || !sceneId || !dashboardId) return;
    overlayRestoredRef.current = true;
    const saved = readSavedPanelPrefs(sceneId);
    if (!saved?.overlayEditMode) return;
    const uiAny = useEditorUiStore as any;
    uiAny.getState?.()?.setOverlayEditMode?.(true);
    if (saved.overlayRightTab) uiAny.getState?.()?.setOverlayRightTab?.(saved.overlayRightTab);
  }, [sceneId, dashboardId]);

  // 写回①：面板定位变化（面板与模型偏好均恢复完成后才写，避免初始空值覆盖存档；
  //        叠加编辑字段仅在恢复决策完成后写，之前保留存档原值）
  useEffect(() => {
    if (!sceneId || !panelRestoredRef.current || !modelPrefsRestoredRef.current) return;
    writePanelPrefsFromStores(sceneId, overlayRestoredRef.current);
  }, [sceneId, activePanel, overlayEditMode, overlayRightTab, dashboardId]);

  // 写回②：模型面板偏好变化（恢复完成后才写）
  useEffect(() => {
    if (!sceneId || !modelPrefsRestoredRef.current) return;
    writePanelPrefsFromStores(sceneId);
  }, [sceneId, expandedModelId, subObjectUnlocked]);
}
